import React, { useState } from 'react';
import { stripeCheckoutService } from '../lib/stripeCheckoutService';
import { TokenPackage } from '../types/auth';

interface CompanyCheckoutFormProps {
  tokenPackage: TokenPackage;
  userId: string;
  onClose: () => void;
  onError: (error: string) => void;
}

export const CompanyCheckoutForm: React.FC<CompanyCheckoutFormProps> = ({
  tokenPackage,
  userId,
  onClose,
  onError
}) => {
  const [isCompanyPurchase, setIsCompanyPurchase] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Add company info to metadata if it's a company purchase
      const enhancedPackage = {
        ...tokenPackage,
        metadata: {
          is_company_purchase: isCompanyPurchase,
          company_name: isCompanyPurchase ? companyName : null
        }
      };

      await stripeCheckoutService.redirectToCheckout(enhancedPackage, userId);
    } catch (error: any) {
      console.error('Error starting checkout:', error);
      onError(error.message || 'Failed to start checkout');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Purchase Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={isProcessing}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {tokenPackage.name} - ${(tokenPackage.price_cents / 100).toFixed(2)}
            </h3>
            <p className="text-gray-600">
              {tokenPackage.tokens} AI Generation Tokens
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="company-purchase"
                checked={isCompanyPurchase}
                onChange={(e) => setIsCompanyPurchase(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="company-purchase" className="ml-2 text-sm text-gray-700">
                This purchase is for a company
              </label>
            </div>

            {isCompanyPurchase && (
              <div className="space-y-2">
                <label htmlFor="company-name" className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter company name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required={isCompanyPurchase}
                />
              </div>
            )}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || (isCompanyPurchase && !companyName.trim())}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                'Continue to Payment'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 